import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaPresentationProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ReCaptcha from "@preconBlueprint/Overrides/Components/ReCaptcha";
import preconSubmitRfqForm from "@preconBlueprint/Store/Pages/Rfq/Handlers/PreconSubmitRfqForm";
import preconValidateRfqForm from "@preconBlueprint/Store/Pages/Rfq/Handlers/PreconValidateRfqForm";
import { PreconState } from "@preconBlueprint/Store/PreconState";
import React, { FocusEvent, RefObject, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    programId = "programId",
    formId = "formId",
    showFileAttachment = "showFileAttachment",
    showOptIn = "showOptIn",
    formType = "formType",
    lastFormURL = "lastFormURL"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.programId]: number;
        [fields.formId]: number;
        [fields.showFileAttachment]: boolean;
        [fields.showOptIn]: boolean;
        [fields.formType]: string;
        [fields.lastFormURL]: string;
    };
}

const mapDispatchToProps = {
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
    preconValidateRfqForm,
    preconSubmitRfqForm,
};

const mapStateToProps = (state: PreconState) => {
    return {
        firstNameErrorMessage: state.preconRfqForm.firstNameErrorMessage,
        companyNameErrorMessage: state.preconRfqForm.companyNameErrorMessage,
        jobTitleErrorMessage: state.preconRfqForm.jobTitleErrorMessage,
        emailErrorMessage: state.preconRfqForm.emailErrorMessage,
        phoneErrorMessage: state.preconRfqForm.phoneErrorMessage,
        countryErrorMessage: state.preconRfqForm.countryErrorMessage,
        stateErrorMessage: state.preconRfqForm.stateErrorMessage,
        addressErrorMessage: state.preconRfqForm.addressErrorMessage,
        cityErrorMessage: state.preconRfqForm.cityErrorMessage,
        postalCodeErrorMessage: state.preconRfqForm.postalCodeErrorMessage,
        quoteDetailsErrorMessage: state.preconRfqForm.quoteDetailsErrorMessage,
        countries: getCurrentCountries(state),
        manufacturerErrorMessage: state.preconRfqForm.manufacturerErrorMessage,
        productCategoryErrorMessage: state.preconRfqForm.productCategoryErrorMessage,
        quantityErrorMessage: state.preconRfqForm.quantityErrorMessage,
        productsOfInterestErrorMessage: state.preconRfqForm.productsOfInterestErrorMessage,
    }
};

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqFormStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    bottomMainGridContainer?: GridContainerProps;
    innerContainer?: GridContainerProps;
    innerGridItem?: GridItemProps;
    outerGridItem?: GridItemProps;
    userGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    userNameGridItem?: GridItemProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    bottomGridItem?: GridItemProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    countrySelect?: SelectPresentationProps;
    stateSelect?: SelectPresentationProps;
    firstNameText?: TextFieldPresentationProps;
    lastNameText?: TextFieldPresentationProps;
    companyNameText?: TextFieldPresentationProps;
    address1Text?: TextFieldPresentationProps;
    address2Text?: TextFieldPresentationProps;
    cityText?: TextFieldPresentationProps;
    postalCodeText?: TextFieldPresentationProps;
    emailTextField?: TextFieldPresentationProps;
    titleText?: TextFieldPresentationProps;
    phoneText?: TextFieldPresentationProps;
    quoteDetailText?: TextAreaPresentationProps;
    submitButton?: ButtonPresentationProps;
    submitErrorTitle?: TypographyProps;
    submittedText?: TypographyProps;
    fileUploader?: FileUploadPresentationProps;
    manufacturerText?: TextFieldPresentationProps;
    productsOfInterestText?: TextFieldPresentationProps;
    quantityText?: TextFieldPresentationProps;
    productCategoryText?: TextFieldPresentationProps;
    rfidCheckbox?: CheckboxPresentationProps;
    checkbox?: CheckboxPresentationProps;
    checkboxGroup?: CheckboxGroupProps;
    innerGridItemWithoutTopPadding?: GridItemProps;
    innerGridItemWithoutBottomPadding?: GridItemProps;
    bottomGridItemWithTopPadding?: GridItemProps;
}

export const rfqFormStyles: RfqFormStyles = {
    mainGridContainer: {
        css: css`
            margin-top: 25px !important;
            background: rgb(239, 241, 247);
            padding: 0 20px;
            border-radius:5px;
        `,
    },
    bottomMainGridContainer: {
        css: css`
            margin-bottom: 20px !important;
            background: rgb(239, 241, 247);
            padding: 0 20px;
            border-radius:5px;
        `,
    },
    outerGridItem: {
        width: [12, 12, 6, 4, 3],
        css: css`
            padding-top: 15px;
        `
    },
    innerContainer: {
        css: css`
            width: 100%;
        `
    },
    innerGridItem: {
        width: 12,
        css: css`
            padding: 15px 15px 0 15px;
        `
    },
    innerGridItemWithoutTopPadding: {
        width: 12,
        css: css`
            padding: 0px 15px 15px 15px;
        `
    },
    innerGridItemWithoutBottomPadding: {
        width: 12,
        css: css`
            padding: 15px 15px 0px 15px;
            font-size: 15px;
        `
    },
    bottomGridItem: {
        width: [12],
        css: css`
            width: 100%;
            margin-top: 12px;
        `
    },
    bottomGridItemWithTopPadding: {
        width: [12],
        css: css`
            width: 100%;
            padding-top: 15px;
        `
    },
    userGridItem: {
        width: [12, 12, 7, 7, 7],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding:20px 0 0 32px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    userGridContainer: {
        css: css`
            width:100%;
        `,
    },
    emailTextField: {
        css: css`
            border-radius:5px !important;
            &::placeholder {
                color:#9b9b9b;
            }
        `
    },
    userNameGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    subscriptionGridItem: {
        width: 12,
        css: css`
            padding:0 15px 15px;
        `,
    },
    reCaptchaGridItem: {
        width: 12,
        css: css`
            padding-top: 0px;
            justify-content: center;
        `
    },
    submitErrorGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `
    },
    submitErrorTitle: {
        color: "danger",
    },
    checkbox: {
        typographyProps: {
            css: css`
                display: flex;
                padding-right:35px;
            `
        }
    },
    checkboxGroup: {
        css: css`
            display: inline-block;
        `
    },
    submittedText: {
        css: css`
            padding-top: 30px;
            font-size: 15px;
            font-weight: 400;
            margin-bottom: 1rem;
            display: block;
        `
    },
    submitGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            padding: 0 15px 30px 15px;
        `,
    },
    submitButton: {
        css: css`
        ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                    width: 100%;
                `,
                    css`
                    width: 100%;
                `,
                    null,
                    null,
                    null,
                ])}
            border:0;
            border-radius:9px;
            padding:0 30px;
            background:#e51937;
            &:hover {
                background:#FA0;
            }
        `,
    },
    fileUploader: {
        buttonProps: {
            variant: "tertiary",
            css: css`
                margin: 32px 0 32px 5px;
                padding: 0 10px;
                width: 100%;
                border-radius: 5px;
            `
        },
    },
};

const styles = rfqFormStyles;
const StyledForm = getStyledWrapper("form");

const RfqForm = ({
    fields,
    validateReCaptcha,
    preconValidateRfqForm,
    preconSubmitRfqForm,
    firstNameErrorMessage,
    companyNameErrorMessage,
    jobTitleErrorMessage,
    emailErrorMessage,
    phoneErrorMessage,
    countryErrorMessage,
    stateErrorMessage,
    addressErrorMessage,
    cityErrorMessage,
    postalCodeErrorMessage,
    quoteDetailsErrorMessage,
    countries,
    manufacturerErrorMessage,
    productCategoryErrorMessage,
    productsOfInterestErrorMessage,
    quantityErrorMessage
}: Props) => {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [jobTitle, setJobTitle] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [address, setAddress] = useState("");
    const [address2, setAddress2] = useState("");
    const [city, setCity] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [quoteDetails, setQuoteDetails] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState<CountryModel | undefined>(undefined);
    const [states, setStates] = useState<StateModel[]>([]);
    const [selectedState, setSelectedState] = useState<StateModel | undefined>(undefined);
    const fileUploadRef = useRef({ value: "" } as HTMLInputElement);
    const [file, setFile] = useState<any>(null);
    const [fileType, setFileType] = useState("");
    const [fileName, setFileName] = useState("");
    const recaptchaRef: React.RefObject<ReCAPTCHA> = React.createRef();
    const [manufacturer, setManufacturer] = useState("");
    const [productsOfInterest, setProductsOfInterest] = useState("");
    const [quantity, setQuantity] = useState(0);
    const [productCategory, setProductCategory] = useState("");
    const [customConvertedParts, setCustomConvertedParts] = useState(false);
    const [adhesiveOrChemicalDownpacking, setAdhesiveOrChemicalDownpacking] = useState(false);
    const [durableLabelsAndIndustrialPrinting, setDurableLabelsAndIndustrialPrinting] = useState(false);
    const [eProcurementOrPunchoutSolutions, setEProcurementOrPunchoutSolutions] = useState(false);
    const [rfid, setRfid] = useState(false);

    useEffect(() => {
        setSelectedCountry(countries?.find(c => c.id === selectedCountry?.id));
    }, [selectedCountry]);

    useEffect(() => {
        setStates(selectedCountry?.states || []);
    }, [selectedCountry]);

    if (!countries) {
        return null;
    }

    const handleTextChange = (event: FocusEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        preconValidateRfqForm({
            [name]: value,
        })
    }

    const handleTextAreaChange = (event: FocusEvent<HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        preconValidateRfqForm({
            [name]: value,
        })
    }

    const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = event.target;
        preconValidateRfqForm({
            [name]: value,
        })
    }

    const firstNameChangeHandler = (event: FocusEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setFirstName(value);
        handleTextChange(event);
    }

    const lastNameChangeHandler = (event: FocusEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setLastName(value);
        handleTextChange(event);
    }

    const companyNameChangeHandler = (event: FocusEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setCompanyName(value);
        handleTextChange(event);
    }

    const emailChangeHandler = (event: FocusEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setEmail(value);
        handleTextChange(event);
    }

    const jobTitleChangeHandler = (event: FocusEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setJobTitle(value);
        handleTextChange(event);
    }

    const phoneChangeHandler = (event: FocusEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setPhone(value);
        handleTextChange(event);
    }

    const countryChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const country = countries?.find(c => c.id === event.target.value);
        setSelectedCountry(country);
        handleSelectChange(event);
    }

    const stateChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const state = selectedCountry?.states?.find(s => s.abbreviation === event.target.value);
        setSelectedState(state);
        handleSelectChange(event);
    }

    const addressChangeHandler = (event: FocusEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setAddress(value);
        handleTextChange(event);
    }

    const cityChangeHandler = (event: FocusEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setCity(value);
        handleTextChange(event);
    }

    const postalCodeChangeHandler = (event: FocusEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setPostalCode(value);
        handleTextChange(event);
    }

    const quoteDetailsChangeHandler = (event: FocusEvent<HTMLTextAreaElement>) => {
        const { value } = event.target;
        setQuoteDetails(value);
        handleTextAreaChange(event);
    }

    const getFileInfo = (data: ArrayBuffer) => {
        let fileInfo = "";
        let l = 0;
        const w = 10240;

        for (; l < data.byteLength / w; ++l) {
            fileInfo += String.fromCharCode.apply(null, new Uint8Array(data.slice(l * w, l * w + w)) as any);
        }

        fileInfo += String.fromCharCode.apply(null, new Uint8Array(data.slice(l * w)) as any);

        return fileInfo;
    };

    const fileChangeHandler = (event: React.ChangeEvent<any>) => {
        const attachment = event.target.files[0];
        if (attachment) {
            setFileName(attachment.name);

            const reader = new FileReader();
            const fileExtension = getFileExtension(attachment.name);
            let fileType = ''

            if (fileExtension.toLowerCase() === "xlsx") {
                fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            }
            if (fileExtension.toLowerCase() === "xls") {
                fileType = "application/vnd.ms-excel";
            }
            if (fileExtension.toLowerCase() === "doc") {
                fileType = "application/msword";
            }
            if (fileExtension.toLowerCase() === "docx") {
                fileType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
            }
            if (fileExtension.toLowerCase() === "pdf") {
                fileType = "application/pdf";
            }
            if (fileExtension.toLowerCase() === "dxf") {
                fileType = "application/dxf";
            }
            if (fileExtension.toLowerCase() === "dwg") {
                fileType = "application/acad";
            }

            reader.readAsArrayBuffer(attachment);

            reader.onloadend = function () {
                if (reader.readyState === 2) {
                    const fileInfo = getFileInfo(reader.result as ArrayBuffer);
                    const file = btoa(fileInfo);

                    setFile(file);
                }
            }

            setFileType(fileType);
        }
    };

    const validateSubmitEnabled = () => {
        const isValid = !(firstNameErrorMessage || companyNameErrorMessage || jobTitleErrorMessage || emailErrorMessage ||
            phoneErrorMessage || countryErrorMessage || stateErrorMessage || addressErrorMessage || cityErrorMessage ||
            postalCodeErrorMessage || quoteDetailsErrorMessage || manufacturerErrorMessage || quantityErrorMessage ||
            productCategoryErrorMessage || productsOfInterestErrorMessage);

        return isValid;
    };

    const isEmptyFields = () => {
        const isValid = firstName !== "" &&
            companyName !== "" &&
            email !== "" &&
            phone !== "" &&
            (selectedCountry || (!selectedCountry && selectedState)) &&
            address !== "" &&
            city !== "" &&
            postalCode !== "" &&
            jobTitle !== "" &&
            quoteDetails !== "";

        return isValid;
    }

    const quantityChangeHandler = (event: FocusEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setQuantity(+value);
        handleTextChange(event);
    }
    const manufacturerChangeHandler = (event: FocusEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setManufacturer(value);
        handleTextChange(event);
    }
    const productCategoryChangeHandler = (event: FocusEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setProductCategory(value);
        handleTextChange(event);
    }
    const productsOfInterestChangeHandler = (event: FocusEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setProductsOfInterest(value);
        handleTextChange(event);
    }
    const address2ChangeHandler = (event: FocusEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setAddress2(value);
        handleTextChange(event);
    }

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const selectedInterests: string[] = [];
        if (rfid) {
            selectedInterests.push('Vendor Managed Inventory/RFID');
        }
        if (customConvertedParts) {
            selectedInterests.push('Custom Converted Parts');
        }
        if (adhesiveOrChemicalDownpacking) {
            selectedInterests.push('Adhesive/Chemical Downpacking');
        }
        if (durableLabelsAndIndustrialPrinting) {
            selectedInterests.push('Durable Labels and Industrial Printing');
        }
        if (eProcurementOrPunchoutSolutions) {
            selectedInterests.push('eProcurement/Punchout Solutions');
        }
        const interestContactUs = (selectedInterests.join(','));
        const isReCaptchaValid = await validateReCaptcha({ location: "RfqForm", recaptchaRef });

        preconValidateRfqForm({
            firstName,
            companyName,
            jobTitle,
            email,
            phone,
            country: selectedCountry?.name || "",
            state: selectedState?.name || "",
            address,
            city,
            postalCode,
            quoteDetails,
            manufacturer,
            quantity,
            productCategory,
            productsOfInterest,
            interestContactUs
        })

        if (!isReCaptchaValid || !validateSubmitEnabled() || !isEmptyFields()) {
            setIsSubmitting(false);
            return;
        }

        setIsSubmitting(true);

        preconSubmitRfqForm({
            rfq: {
                programId: fields.programId,
                formId: fields.formId,
                firstName,
                lastName,
                company: companyName,
                title: jobTitle,
                email,
                phone,
                country: selectedCountry?.name,
                state: selectedState?.name,
                address1: address,
                city,
                zip: postalCode,
                notes: quoteDetails,
                isSubscribed,
                file,
                fileName,
                fileType,
                productCategory,
                productsOfInterest,
                quantity,
                manufacturer,
                interestContactUs,
                lastFormURL: fields.lastFormURL,
                formType: fields.formType,
                address2,
            },
            onSuccess: () => {
                setErrorMessage("");
                setIsSubmitted(true)
                setIsSubmitting(false);
            },
            onError: (errorMessage: string) => {
                setIsSubmitted(false);
                setErrorMessage(errorMessage);
            }
        });
    };

    return (
        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
            {isSubmitted && (
                <GridContainer {...styles.mainGridContainer}>
                    <GridItem {...styles.submitGridItem}>
                        <Typography as="p" {...styles.submittedText}>
                            {translate("Thank you! You have successfully submitted your request, and a Hisco representative will respond to you as quickly as possible.")}
                        </Typography>
                    </GridItem>
                </GridContainer>
            )}
            {!isSubmitted && (
                <GridContainer {...styles.mainGridContainer}>
                    <GridItem {...styles.outerGridItem}>
                        <GridContainer {...styles.innerContainer}>
                            <GridItem {...styles.innerGridItem}>
                                <TextField
                                    {...styles.firstNameText}
                                    name="firstName"
                                    label={translate("First name")}
                                    value={firstName}
                                    maxLength={50}
                                    onChange={firstNameChangeHandler}
                                    error={firstNameErrorMessage}
                                    data-test-selector="rfqForm_firstName"
                                    required={true}
                                />
                            </GridItem>
                            <GridItem {...styles.innerGridItem}>
                                <TextField
                                    {...styles.emailTextField}
                                    type="email"
                                    name="email"
                                    label={translate("Business Email")}
                                    maxLength={50}
                                    onChange={emailChangeHandler}
                                    value={email}
                                    error={emailErrorMessage}
                                    data-test-selector="rfqForm_email"
                                    required={true}
                                />
                            </GridItem>
                            <GridItem {...styles.innerGridItem}>
                                <TextField
                                    {...styles.address1Text}
                                    name="address"
                                    label={translate("Street address")}
                                    value={address}
                                    maxLength={75}
                                    onChange={addressChangeHandler}
                                    error={addressErrorMessage}
                                    data-test-selector="rfqForm_address1"
                                    required={true}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.outerGridItem}>
                        <GridContainer {...styles.innerContainer}>
                            <GridItem {...styles.innerGridItem}>
                                <TextField
                                    {...styles.lastNameText}
                                    name="lastName"
                                    label={translate("Last name")}
                                    value={lastName}
                                    maxLength={50}
                                    onChange={lastNameChangeHandler}
                                    data-test-selector="rfqForm_lastName"
                                />
                            </GridItem>
                            <GridItem {...styles.innerGridItem}>
                                <TextField
                                    {...styles.phoneText}
                                    name="phone"
                                    label={translate("Phone number")}
                                    value={phone}
                                    maxLength={25}
                                    onChange={phoneChangeHandler}
                                    error={phoneErrorMessage}
                                    data-test-selector="rfqForm_phone"
                                    required={true}
                                />
                            </GridItem>
                            <GridItem {...styles.innerGridItem}>
                                <TextField
                                    {...styles.address2Text}
                                    name="address2"
                                    label={translate ("Street address 2")}
                                    value={address2}
                                    maxLength={75}
                                    onChange={address2ChangeHandler}
                                    data-test-selector="rfqForm_address2"
                                />                                
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.outerGridItem}>
                        <GridContainer {...styles.innerContainer}>
                            <GridItem {...styles.innerGridItem}>
                                <TextField
                                    {...styles.companyNameText}
                                    name="companyName"
                                    label={translate("Company name")}
                                    value={companyName}
                                    maxLength={25}
                                    onChange={companyNameChangeHandler}
                                    error={companyNameErrorMessage}
                                    data-test-selector="rfqForm_companyName"
                                    required={true}
                                />
                            </GridItem>
                            <GridItem {...styles.innerGridItem}>
                                <Select
                                    {...styles.countrySelect}
                                    name="country"
                                    label={translate("Country/Region")}
                                    value={selectedCountry?.id || ""}
                                    onChange={countryChangeHandler}
                                    error={countryErrorMessage}
                                    data-test-selector="rfqForm_country"
                                    required={true}
                                >
                                    <option value="">{translate("Select Country")}</option>
                                    {countries?.map(co => (
                                        <option key={co.id} value={co.id}>
                                            {co.name}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                            <GridItem {...styles.innerGridItem}>
                                <TextField
                                    {...styles.cityText}
                                    name="city"
                                    label={translate ("City")}
                                    value={city}
                                    maxLength={50}
                                    onChange={cityChangeHandler}
                                    error={cityErrorMessage}
                                    data-test-selector="rfqForm_city"
                                    required={true}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.outerGridItem}>
                        <GridContainer {...styles.innerContainer}>
                            <GridItem {...styles.innerGridItem}>
                                <TextField
                                    {...styles.titleText}
                                    name="jobTitle"
                                    label={translate("Job Role")}
                                    value={jobTitle}
                                    maxLength={25}
                                    onChange={jobTitleChangeHandler}
                                    error={jobTitleErrorMessage}
                                    required={true}
                                    data-test-selector="rfqForm_jobTitle"
                                />
                            </GridItem>
                            <GridItem {...styles.innerGridItem}>
                                <Select
                                    {...styles.stateSelect}
                                    label={translate("State")}
                                    name="state"
                                    value={selectedState?.abbreviation || ""}
                                    onChange={stateChangeHandler}
                                    error={stateErrorMessage}
                                    data-test-selector="rfqForm_state"
                                    disabled={!selectedCountry || (selectedCountry && selectedCountry.abbreviation.toUpperCase() !== "US" && selectedCountry.abbreviation.toUpperCase() !== "MX")}
                                    required={true}
                                >
                                    <option value="">{translate("Select State")}</option>
                                    {states.map(st => (
                                        <option key={st.id} value={st.abbreviation}>
                                            {st.abbreviation}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                            <GridItem {...styles.innerGridItem}>
                                <TextField
                                    {...styles.postalCodeText}
                                    name="postalCode"
                                    label={translate ("Postal code")}
                                    value={postalCode}
                                    maxLength={10}
                                    onChange={postalCodeChangeHandler}
                                    error={postalCodeErrorMessage}
                                    data-test-selector="rfqForm_postalCode"
                                    required={true}
                                />
                            </GridItem>                                                      
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.outerGridItem}>
                        <GridContainer {...styles.innerContainer}>
                            <GridItem {...styles.innerGridItem}>
                                <FileUpload
                                    {...styles.fileUploader}
                                    inputRef={fileUploadRef}
                                    fileName={file?.name || ""}
                                    accept=".xls,.xlsx,.csv,.doc,.docx,.pdf,.dxf,.dwg"
                                    label={translate("Add an attachment")}
                                    labelPosition="top"
                                    // error={incorrectFileExtension ? translate("must be .xls, .xlsx or .csv") : undefined}
                                    onFileChange={fileChangeHandler}
                                    uid="rfqUploadFileUpload"
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>  
                    <GridItem {...styles.bottomGridItem}>
                        <GridContainer {...styles.innerContainer}>
                            <GridItem {...styles.innerGridItemWithoutBottomPadding}>
                                <div >
                                    <strong><span>Contact is regarding </span></strong>
                                    <i><span>(select all that apply):</span></i>
                                    <strong><span> *</span></strong>
                                </div>
                            </GridItem>
                            <GridItem {...styles.innerGridItemWithoutTopPadding}>
                                <CheckboxGroup {...styles.checkboxGroup}>
                                    <Checkbox
                                        {...styles.checkbox}
                                        onChange={(e, value) => setRfid(value)}
                                        checked={rfid}
                                        data-test-selector="rfqForm_RFID"
                                    >
                                        {translate("Vendor Managed Inventory/RFID")}
                                    </Checkbox>
                                    <Checkbox
                                        {...styles.checkbox}
                                        onChange={(e, value) => setCustomConvertedParts(value)}
                                        checked={customConvertedParts}
                                        data-test-selector="rfqForm_customConvertedParts"
                                    >
                                        {translate("Custom Converted Parts")}
                                    </Checkbox>
                                    <Checkbox
                                        {...styles.checkbox}
                                        onChange={(e, value) => setAdhesiveOrChemicalDownpacking(value)}
                                        checked={adhesiveOrChemicalDownpacking}
                                        data-test-selector="rfqForm_adhesiveOrChemicalDownpacking"
                                    >
                                        {translate("Adhesive/Chemical Downpacking")}
                                    </Checkbox>
                                    <Checkbox
                                        {...styles.checkbox}
                                        onChange={(e, value) => setDurableLabelsAndIndustrialPrinting(value)}
                                        checked={durableLabelsAndIndustrialPrinting}
                                        data-test-selector="rfqForm_durableLabelsAndIndustrialPrinting"
                                    >
                                        {translate("Durable Labels and Industrial Printing")}
                                    </Checkbox>
                                    <Checkbox
                                        {...styles.checkbox}
                                        onChange={(e, value) => setEProcurementOrPunchoutSolutions(value)}
                                        checked={eProcurementOrPunchoutSolutions}
                                        data-test-selector="rfqForm_eProcurementOrPunchoutSolutions"
                                    >
                                        {translate("eProcurement/Punchout Solutions")}
                                    </Checkbox>
                                </CheckboxGroup>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridContainer {...styles.bottomMainGridContainer}>
                        <GridItem {...styles.outerGridItem}>
                            <GridContainer {...styles.innerContainer}>
                                <GridItem {...styles.innerGridItem}>
                                    <TextField
                                        {...styles.manufacturerText}
                                        name="manufacturer"
                                        label={translate("Manufacturer")}
                                        value={manufacturer}
                                        maxLength={50}
                                        onChange={manufacturerChangeHandler}
                                        error={manufacturerErrorMessage}
                                        data-test-selector="rfqForm_manufacturer"
                                        required={true}
                                    />
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.outerGridItem}>
                            <GridContainer {...styles.innerContainer}>
                                <GridItem {...styles.innerGridItem}>
                                    <TextField
                                        {...styles.productCategoryText}
                                        name="productCategory"
                                        label={translate("Product Category")}
                                        value={productCategory}
                                        maxLength={250}
                                        onChange={productCategoryChangeHandler}
                                        error={productCategoryErrorMessage}
                                        data-test-selector="rfqForm_productCategory"
                                        required={true}
                                    />
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.outerGridItem}>
                            <GridContainer {...styles.innerContainer}>
                                <GridItem {...styles.innerGridItem}>
                                    <TextField
                                        {...styles.productsOfInterestText}
                                        name="productsOfInterest"
                                        label={translate("Products Of Interest")}
                                        value={productsOfInterest}
                                        maxLength={300}
                                        onChange={productsOfInterestChangeHandler}
                                        error={productsOfInterestErrorMessage}
                                        data-test-selector="rfqForm_productsOfInterest"
                                        required={true}
                                    />
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.outerGridItem}>
                            <GridContainer {...styles.innerContainer}>
                                <GridItem {...styles.innerGridItem}>
                                    <TextField
                                        {...styles.quantityText}
                                        name="quantity"
                                        type="number"
                                        label={translate("Quantity")}
                                        value={quantity}
                                        min={0}
                                        onChange={quantityChangeHandler}
                                        error={quantityErrorMessage}
                                        data-test-selector="rfqForm_quantity"
                                        required={true}
                                    />
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                    <GridItem {...styles.bottomGridItem}>
                        <GridContainer {...styles.innerContainer}>
                            <GridItem {...styles.innerGridItem}>
                                <TextArea
                                    {...styles.quoteDetailText}
                                    name="quoteDetails"
                                    label={translate("Quote Details")}
                                    value={quoteDetails}
                                    maxLength={250}
                                    onChange={quoteDetailsChangeHandler}
                                    error={quoteDetailsErrorMessage}
                                    data-test-selector="rfqForm_quoteDetails"
                                    required={true}
                                />
                            </GridItem>
                            <GridItem {...styles.innerGridItem}>
                                <CheckboxGroup>
                                    <Checkbox
                                        onChange={(e, value) => setIsSubscribed(value)}
                                        checked={isSubscribed}
                                        data-test-selector="rfqForm_Subscription"
                                    >
                                        {translate("I would like to receive special offers, product notifications and other news from Hisco. We do not provide your information to 3rd parties.")}
                                    </Checkbox>
                                </CheckboxGroup>
                            </GridItem>
                            <GridItem {...styles.innerGridItem}>
                                <GridItem {...styles.reCaptchaGridItem}>
                                    <ReCaptcha recaptchaRef={recaptchaRef} location="RfqForm" byPassIsEnabled={true} byPassIsAuthenticated={true} />
                                </GridItem>
                            </GridItem>
                            {errorMessage && (
                                <GridItem data-test-selector="rfqForm_ErrorMessage" {...styles.submitErrorGridItem}>
                                    <Typography {...styles.submitErrorTitle}>{errorMessage}</Typography>
                                </GridItem>
                            )}
                            <GridItem {...styles.submitGridItem}>
                                <Button
                                    {...styles.submitButton}
                                    type="submit"
                                    disabled={isSubmitting || !validateSubmitEnabled()}
                                    data-test-selector="rfqForm_createButton"
                                >
                                    {translate("Submit RFQ")}
                                </Button>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            )}
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RfqForm),
    definition: {
        group: "Basic",
        displayName: "RFQ Form",
        fieldDefinitions: [
            {
                name: fields.programId,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                displayName: "Marketo Program ID",
            },
            {
                name: fields.formId,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                displayName: "Marketo Form ID",
                isRequired: true,
            },
            {
                name: fields.showFileAttachment,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                displayName: "Show Attachment Field",
                isRequired: true,
            },
            {
                name: fields.showOptIn,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                displayName: "Show Opt-In Field",
                isRequired: true,
            },
            {
                name: fields.formType,
                fieldType: "General",
                displayName: "Form Type",
                editorTemplate: "TextField",
                defaultValue: "RFQ",
            },
            {
                name: fields.lastFormURL,
                fieldType: "General",
                displayName: "Last Form Url",
                editorTemplate: "TextField",
                defaultValue: "Last Form Url",
            }
        ],
    },
};

export default widgetModule;
